import { useState, useEffect } from 'react';
import useCurrentUser from '../../hooks/useCurrentUser';
import PatchNotesModal from './PatchNotesModal';
import useAcl from '../../hooks/useAcl';

const menuGroups = [
    {
        label: 'Planning',
        items: [
            { id: 'planning', icon: 'ti-calendar', label: 'Planning', permission: 'voir_planning' },
        ]
    },
    {
        label: 'Patients',
        items: [
            { id: 'patients',    icon: 'ti-stethoscope', label: 'Patients',      permission: 'voir_patients' },
            { id: 'recurrences', icon: 'ti-repeat',       label: 'Récurrences',   permission: 'voir_recurrences' },
            { id: 'map',         icon: 'ti-map-pin',      label: 'Carte patients', permission: 'voir_carte' },
        ]
    },
    {
        label: 'Administration',
        superAdminOnly: true,
        items: [
            { id: 'users',   icon: 'ti-users',        label: 'Utilisateurs', superAdminOnly: true },
            { id: 'secteurs', icon: 'ti-map',         label: 'Secteurs' },
            { id: 'agences', icon: 'ti-building',     label: 'Agences',      superAdminOnly: true },
            { id: 'acl',     icon: 'ti-shield-lock',  label: 'Permissions',  superAdminOnly: true },
            { id: 'bug-reports', icon: 'ti-bug', label: 'Rapports bugs', superAdminOnly: true },
        ]
    },
    {
        label: 'Rapports',
        items: [
            { id: 'stats', icon: 'ti-chart-bar',      label: 'Statistiques', permission: 'voir_stats' },
            { id: 'logs',  icon: 'ti-clipboard-list', label: 'Logs',         permission: 'voir_logs' },
        ]
    },
];

export default function Sidebar({ activePage, onNavigate, onLogout }) {
    const [expanded, setExpanded] = useState(true);
    const [showPatchNotes, setShowPatchNotes] = useState(false);
    const currentUser = useCurrentUser();
    const [appVersion, setAppVersion] = useState('');
    const { can, aclLoading } = useAcl();

    useEffect(() => {
        fetch('/version.json?t=' + Date.now())
            .then(r => r.json())
            .then(data => setAppVersion(data.version))
            .catch(() => {});
    }, []);

    return (
        <div style={{
            width: expanded ? '200px' : '56px',
            height: '100vh',
            position: 'sticky',
            top: 0,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#1a1a2e',
            transition: 'width 0.2s ease',
            flexShrink: 0,
            zIndex: 100,
        }}>
            {/* Logo + toggle */}
            <div
                onClick={() => setExpanded(!expanded)}
                style={{
                    padding: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    cursor: 'pointer',
                    borderBottom: '1px solid rgba(255,255,255,0.1)',
                    minHeight: '56px',
                    flexShrink: 0,
                }}
            >
                <svg width="20" height="20" viewBox="0 0 60 60" style={{flexShrink: 0}}>
                    <circle cx="30" cy="30" r="30" fill="#4f46e5"/>
                    <g transform="translate(30,30)">
                        <rect x="-2.3" y="-10.5" width="4.6" height="21" rx="2.3" fill="white"/>
                        <rect x="-10.5" y="-2.3" width="21" height="4.6" rx="2.3" fill="white"/>
                        <path d="M6.5 6.5 Q10.5 9 12.5 12.5" fill="none" stroke="white" strokeWidth="1.8"
                              strokeDasharray="1.8 2.5" strokeLinecap="round"/>
                        <circle cx="12.5" cy="12.5" r="3" fill="white"/>
                    </g>
                </svg>
                {expanded && (
                    <span style={{color: 'white', fontWeight: '700', fontSize: '15px', whiteSpace: 'nowrap'}}>
            Planning
        </span>
                )}
                {expanded && (
                    <span style={{color: 'rgba(255,255,255,0.4)', marginLeft: 'auto', fontSize: '12px'}}>◀</span>
                )}
            </div>

            {/* Menu items groupés */}
            <nav style={{ flex: 1, paddingTop: '8px', overflowY: 'auto' }}>
                {aclLoading ? (
                    <div style={{ padding: '20px 16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {[1,2,3,4,5].map(i => (
                            <div key={i} style={{
                                height: '36px', borderRadius: '8px',
                                backgroundColor: 'rgba(255,255,255,0.05)',
                                animation: 'pulse 1.5s ease-in-out infinite',
                            }} />
                        ))}
                    </div>
                ) : menuGroups.map(group => {
                    // Filtre les items selon les droits
                    const visibleItems = group.items.filter(item => {
                        if (item.superAdminOnly && !currentUser?.isSuperAdmin) return false;
                        if (item.permission && !can(item.permission)) return false;
                        return true;
                    });

                    // Cache le groupe si aucun item visible
                    if (visibleItems.length === 0) return null;
                    if (group.superAdminOnly && !currentUser?.isSuperAdmin) return null;

                    return (
                        <div key={group.label}>
                            {/* Label du groupe */}
                            {expanded && (
                                <div style={{
                                    padding: '10px 16px 4px',
                                    fontSize: '10px',
                                    fontWeight: '700',
                                    color: 'rgba(255,255,255,0.3)',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.08em',
                                }}>
                                    {group.label}
                                </div>
                            )}
                            {visibleItems.map(item => (
                                <div
                                    key={item.id}
                                    onClick={() => onNavigate(item.id)}
                                    title={!expanded ? item.label : ''}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px',
                                        padding: '10px 16px',
                                        cursor: 'pointer',
                                        borderLeft: activePage === item.id ? `3px solid var(--accent-color)` : '3px solid transparent',
                                        backgroundColor: activePage === item.id ? 'rgba(79,70,229,0.3)' : 'transparent',
                                        transition: 'background-color 0.15s',
                                    }}
                                    onMouseEnter={e => {
                                        if (activePage !== item.id) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)';
                                    }}
                                    onMouseLeave={e => {
                                        if (activePage !== item.id) e.currentTarget.style.backgroundColor = 'transparent';
                                    }}
                                >
                                    <i className={`ti ${item.icon}`} style={{ fontSize: '18px', flexShrink: 0, color: 'rgba(255,255,255,0.85)' }} />
                                    {expanded && (
                                        <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '14px', whiteSpace: 'nowrap' }}>
                                {item.label}
                            </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    );
                })}
            </nav>

            {/* Hauts faits — juste avant Nouveautés */}
            <div
                onClick={() => onNavigate('achievements')}
                title={!expanded ? 'Hauts faits' : ''}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 16px',
                    cursor: 'pointer',
                    borderLeft: activePage === 'achievements' ? `3px solid var(--accent-color)` : '3px solid transparent',
                    backgroundColor: activePage === 'achievements' ? 'rgba(79,70,229,0.3)' : 'transparent',
                    borderTop: '1px solid rgba(255,255,255,0.1)',
                    flexShrink: 0,
                }}
                onMouseEnter={e => {
                    if (activePage !== 'achievements') e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)';
                }}
                onMouseLeave={e => {
                    if (activePage !== 'achievements') e.currentTarget.style.backgroundColor = 'transparent';
                }}
            >
                <i className="ti ti-trophy" style={{fontSize: '18px', flexShrink: 0, color: 'rgba(255,255,255,0.85)'}}/>
                {expanded && (
                    <span style={{color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>Hauts faits</span>
                )}
            </div>

            {currentUser?.isSuperAdmin && (
                <div
                    onClick={() => onNavigate('achievements-wiki')}
                    title={!expanded ? 'Wiki hauts faits' : ''}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px 16px',
                        cursor: 'pointer',
                        borderLeft: activePage === 'achievements-wiki' ? `3px solid var(--accent-color)` : '3px solid transparent',
                        backgroundColor: activePage === 'achievements-wiki' ? 'rgba(79,70,229,0.3)' : 'transparent',
                        flexShrink: 0,
                    }}
                    onMouseEnter={e => {
                        if (activePage !== 'achievements-wiki') e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)';
                    }}
                    onMouseLeave={e => {
                        if (activePage !== 'achievements-wiki') e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                >
                    <i className="ti ti-book" style={{fontSize: '18px', flexShrink: 0, color: 'rgba(255,255,255,0.85)'}}/>
                    {expanded && (
                        <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>Wiki hauts faits</span>
                    )}
                </div>
            )}

            {/* Nouveautés — juste avant la déconnexion */}
            <div
                onClick={() => setShowPatchNotes(true)}
                title={!expanded ? 'Nouveautés' : ''}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 16px',
                    cursor: 'pointer',
                    borderTop: '1px solid rgba(255,255,255,0.1)',
                    flexShrink: 0,
                }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
            >
                <i className="ti ti-rocket" style={{fontSize: '18px', flexShrink: 0, color: 'rgba(255,255,255,0.85)'}}/>
                {expanded && (
                    <span style={{color: 'rgba(255,255,255,0.6)', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>Nouveautés
                        {appVersion && <span style={{fontSize: '10px', color: 'rgba(255,255,255,0.3)'}}>v{appVersion}</span>}</span>
                )}
            </div>

            {/* Déconnexion — toujours en bas */}
            <div
                onClick={onLogout}
                title={!expanded ? 'Déconnexion' : ''}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 16px',
                    cursor: 'pointer',
                    borderTop: '1px solid rgba(255,255,255,0.1)',
                    flexShrink: 0,
                }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
            >
                <i className="ti ti-logout" style={{fontSize: '18px', flexShrink: 0, color: 'rgba(255,255,255,0.85)'}}/>
                {expanded && (
                    <span style={{color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>Déconnexion</span>
                )}
            </div>
            {showPatchNotes && <PatchNotesModal onClose={() => setShowPatchNotes(false)} />}
        </div>
    );
}